import { useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell,
} from "recharts";
import { CheckCircle2, TrendingUp, TrendingDown, ChevronDown } from "lucide-react";
import { useData } from "@/context/DataContext";
import { Card } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Loading } from "@/pages/_shared";

const TABS = ["Overview", "Reports", "Transactions", "Account balances"];
const PALETTE = ["#1a5fd0", "#3b7ae0", "#5f97ea", "#8ab6f2", "#b3ccf5", "#94a3b8"];

function fmt(n) {
  return `$${Math.round(n).toLocaleString()}`;
}

function DeltaTag({ pct }) {
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-[12px] font-semibold ${up ? "text-emerald-600" : "text-red-500"}`}>
      {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{Math.abs(pct)}%
      <span className="ml-1 font-sans font-normal text-slate-400">vs. May</span>
    </span>
  );
}

function FlowCard({ testid, ring, label, value, delta, catLabel, catValue, uncatValue }) {
  return (
    <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center" data-testid={testid}>
      <div className="flex flex-1 items-center gap-4">
        <div className="relative h-11 w-11 shrink-0">
          <svg viewBox="0 0 36 36" className="h-11 w-11 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#e2e8f0" strokeWidth="4" />
            <circle cx="18" cy="18" r="15" fill="none" stroke={ring} strokeWidth="4" strokeLinecap="round" strokeDasharray={`${0.78 * 94} 94`} />
          </svg>
        </div>
        <div>
          <div className="text-[13px] font-medium text-slate-500">{label}</div>
          <div className="font-head text-xl font-extrabold tabular-nums text-slate-900">{value}</div>
        </div>
      </div>
      {delta != null && <div className="w-28 shrink-0"><DeltaTag pct={delta} /></div>}
      {catLabel && (
        <div className="flex flex-1 items-center gap-6">
          <div>
            <div className="flex items-center gap-1.5 text-[12px] text-slate-500"><span className="h-2 w-2 rounded-full" style={{ background: ring }} />{catLabel}</div>
            <div className="font-head text-[15px] font-bold text-slate-900">{catValue}</div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[12px] text-slate-400"><span className="h-2 w-2 rounded-full bg-slate-300" />Uncategorized</div>
            <div className="font-head text-[15px] font-bold text-slate-500">{uncatValue}</div>
          </div>
        </div>
      )}
    </Card>
  );
}

function Donut({ testid, title, total, rows, rowTestid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-head text-[15px] font-extrabold text-slate-900">{title}</h3>
        <button className="text-[12px] font-semibold text-[#1a5fd0] hover:underline">View transactions</button>
      </div>
      <div className="relative mx-auto h-48 w-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="amount" nameKey="category" innerRadius={62} outerRadius={82} paddingAngle={2} stroke="none">
              {rows.map((r, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => fmt(v * 1000)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[11px] text-slate-400">Total</span>
          <span className="font-head text-lg font-extrabold text-slate-900">{fmt(total * 1000)}</span>
        </div>
      </div>
      <div className="mt-4 divide-y divide-slate-100">
        <div className="flex items-center gap-2 pb-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
          <span className="flex-1">Category</span><span className="w-20 text-right">Value</span><span className="w-10 text-right">%</span>
        </div>
        {rows.map((r, i) => (
          <div key={r.category} className="flex items-center gap-2 py-2 text-[13px]" data-testid={rowTestid}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="flex-1 text-slate-700">{r.category}</span>
            <span className="w-20 text-right font-mono tabular-nums text-slate-800">{fmt(r.amount * 1000)}</span>
            <span className="w-10 text-right font-mono text-slate-400">{Math.round((r.amount / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function CashFlow() {
  const { snapshot } = useData();
  const [tab, setTab] = useState("Overview");
  if (!snapshot) return <Loading />;
  const cf = snapshot.cash_flow;

  if (!snapshot.is_demo || !cf.timeline || cf.timeline.length < 2) {
    return (
      <div className="mx-auto max-w-5xl animate-fade-up" data-testid="cash-flow-page">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Cash flow</h1>
          <StatusChip state={cf.state} />
        </div>
        <Card className="mt-6 flex flex-col items-center justify-center gap-2 py-20 text-center" data-testid="cf-empty">
          <div className="font-head text-lg font-bold text-slate-800">No cash-flow data yet</div>
          <p className="max-w-sm text-[13px] text-slate-500">Connect a bank account to view balances, income, expenses and cash-flow insights. No estimated or sample figures are shown.</p>
        </Card>
      </div>
    );
  }

  const revenueRows = cf.money_map.filter((x) => x.type === "in");
  const expenseRows = cf.money_map.filter((x) => x.type === "out").sort((a, b) => b.amount - a.amount);
  const moneyIn = revenueRows.reduce((s, x) => s + x.amount, 0);
  const moneyOut = expenseRows.reduce((s, x) => s + x.amount, 0);
  const net = moneyIn - moneyOut;

  const t = cf.timeline;
  const last = t[t.length - 1], prev = t[t.length - 2];
  const inDelta = Math.round(((last.revenue - prev.revenue) / prev.revenue) * 100);
  const outDelta = Math.round(((last.expenses - prev.expenses) / prev.expenses) * 100);
  const netDelta = Math.round((((last.revenue - last.expenses) - (prev.revenue - prev.expenses)) / Math.abs(prev.revenue - prev.expenses)) * 100);
  const chartData = t.map((x) => ({ month: x.month, revenue: x.revenue, expenses: x.expenses, net: x.revenue - x.expenses }));

  return (
    <div className="mx-auto max-w-5xl animate-fade-up" data-testid="cash-flow-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Cash flow</h1>
        <StatusChip state={cf.state} />
      </div>

      <div className="mt-4 flex gap-6 border-b border-slate-200">
        {TABS.map((x) => (
          <button
            key={x}
            onClick={() => setTab(x)}
            data-testid={`cf-tab-${x.toLowerCase().replace(/\s+/g, "-")}`}
            className={`-mb-px flex items-center gap-2 border-b-2 pb-2.5 text-[14px] font-semibold transition-colors ${
              tab === x ? "border-[#1a5fd0] text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            {x}{x === "Transactions" && <span className="rounded-full bg-slate-200 px-1.5 text-[10px] font-bold text-slate-600">3</span>}
          </button>
        ))}
      </div>

      {tab !== "Overview" ? (
        <Card className="mt-6 flex flex-col items-center justify-center gap-2 py-20 text-center" data-testid="cf-placeholder">
          <StatusChip state="SANDBOX" />
          <div className="mt-2 font-head text-lg font-bold text-slate-800">{tab}</div>
          <p className="max-w-sm text-[13px] text-slate-500">This view activates once open-finance providers are connected. All figures shown elsewhere are sandbox demo data.</p>
        </Card>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="flex items-center justify-between">
            <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700" data-testid="cf-month-select">
              June 2026 <ChevronDown size={15} className="text-slate-400" />
            </button>
            <button className="text-[13px] font-semibold text-[#1a5fd0] hover:underline">Cash flow settings</button>
          </div>

          <div className="flex items-center justify-between rounded-xl bg-slate-100 px-5 py-3.5" data-testid="cf-review-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={18} className="text-slate-500" />
              <div>
                <div className="text-[13px] font-bold text-slate-800">3 transactions need to be confirmed for this month</div>
                <div className="text-[12px] text-slate-500">Confirm transactions to see accurate category reports</div>
              </div>
            </div>
            <button className="text-[13px] font-semibold text-[#1a5fd0] hover:underline">Review</button>
          </div>

          <FlowCard testid="cf-money-in" ring="#059669" label="Money in" value={fmt(moneyIn * 1000)} delta={inDelta}
            catLabel="Categorized revenue" catValue={fmt(moneyIn * 1000)} uncatValue="$0" />
          <FlowCard testid="cf-money-out" ring="#1a5fd0" label="Money out" value={fmt(moneyOut * 1000)} delta={outDelta}
            catLabel="Categorized expenses" catValue={fmt(moneyOut * 1000)} uncatValue="$0" />
          <FlowCard testid="cf-net" ring="#059669" label="Net cash flow" value={fmt(net * 1000)} delta={netDelta}
            catLabel="Categorized net profit" catValue={fmt(net * 1000)} uncatValue="$0" />

          <Card className="p-5">
            <h3 className="mb-4 font-head text-[15px] font-extrabold text-slate-900">Revenue vs. expenses</h3>
            <div className="h-72" data-testid="cf-rev-exp-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}K`} />
                  <Tooltip formatter={(v) => fmt(v * 1000)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#b3ccf5" radius={[3, 3, 0, 0]} barSize={16} />
                  <Bar dataKey="expenses" name="Expenses" fill="#1a5fd0" radius={[3, 3, 0, 0]} barSize={16} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="#0f172a" strokeWidth={2} dot={{ r: 3, fill: "#fff", stroke: "#0f172a" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex justify-center gap-6 text-[12px] text-slate-500">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#b3ccf5]" />Revenue</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#1a5fd0]" />Expenses</span>
              <span className="flex items-center gap-1.5"><span className="h-0.5 w-3 bg-slate-900" />Net</span>
            </div>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Donut testid="cf-revenue-donut" title="Revenue" total={moneyIn} rows={revenueRows} rowTestid="cf-revenue-row" />
            <Donut testid="cf-expense-donut" title="Expenses" total={moneyOut} rows={expenseRows} rowTestid="cf-expense-row" />
          </div>

          <p className="pb-4 text-center font-mono text-[10px] uppercase tracking-wider text-slate-400">
            Sandbox data — open-finance figures activate once providers are connected
          </p>
        </div>
      )}
    </div>
  );
}
